
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import CustomerForm, { CustomerFormValues } from './CustomerForm';
import { createCustomer } from '@/services/customerService';
import { useAuth } from '@/hooks/useAuth';

interface CreateCustomerDialogProps {
  onCustomerCreated: (customer: any) => void;
}

const CreateCustomerDialog: React.FC<CreateCustomerDialogProps> = ({ onCustomerCreated }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (data: CustomerFormValues) => {
    setIsLoading(true);
    try {
      // Check if user is authenticated
      if (!user) {
        toast.error('You must be logged in to create a customer');
        return;
      }

      const customerData = {
        customer_name: data.customer_name,
        customer_number: data.customer_number, // This can be empty, the backend will generate one
        customer_type: data.customer_type,
        phone: data.phone || undefined,
        email: data.email || undefined,
        organisation_number: data.organisation_number || undefined,
        contact_firstname: data.contact_firstname || undefined,
        contact_lastname: data.contact_lastname || undefined,
        postal_street: data.postal_street || undefined,
        postal_zip_code: data.postal_zip_code || undefined,
        postal_city: data.postal_city || undefined,
        first_name: data.first_name || undefined,
        last_name: data.last_name || undefined
      };
      
      const newCustomer = await createCustomer(customerData);
      toast.success('Customer created successfully');
      onCustomerCreated(newCustomer);
      setOpen(false);
    } catch (error) {
      console.error('Error creating customer:', error);
      if (error instanceof Error) {
        toast.error(`Failed to create customer: ${error.message}`);
      } else {
        toast.error('Failed to create customer');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="h-4 w-4 mr-2" />
          New Customer
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create new customer</DialogTitle>
        </DialogHeader>
        <CustomerForm 
          onSubmit={handleSubmit} 
          isLoading={isLoading} 
          submitButtonText="Create Customer"
          id="create-customer-form"
          hideCustomerNumber={true} // We'll generate the customer number automatically
        />
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomerDialog;
